// Pure helpers for the Our Work view

import { FILTER_ALL, FILTER_OTHERS } from './constants';

const getRichTextHTML = (field) => {
  if (!field) return null;
  return field.output || field.data || null;
};

const hasSectorTitles = (titles) => {
  if (Array.isArray(titles)) return titles.length > 0;
  return typeof titles === 'string' && titles.length > 0;
};

const matchesProjectStatus = (item, projectSubFilter) => {
  if (projectSubFilter === 'all') return true;
  if (projectSubFilter === 'coordination') return Boolean(item.is_coordinated);

  const endDate = item.end_date ? new Date(item.end_date) : null;
  const isFinished = endDate && endDate <= new Date();

  if (projectSubFilter === 'finished') return Boolean(isFinished);
  if (projectSubFilter === 'ongoing') return !isFinished;
  return true;
};

const getCardLabel = (item) => {
  const type = item['@type'];
  if (type === 'project' && item.is_coordinated) return 'COORDINATED';
  if (type === 'service' && item.is_operational) return 'OPERATIONAL';
  return '';
};

const formatProjectDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    month: '2-digit',
    year: 'numeric',
  });
};

const getCardCaption = (item) => {
  if (item['@type'] !== 'project') return '';

  let state = 'ONGOING';

  const endDate = item.end_date ? new Date(item.end_date) : null;
  if (endDate && endDate <= new Date()) {
    const from = formatProjectDate(item.start_date);
    const to = item.end_date ? formatProjectDate(item.end_date) : 'Present';

    state = `FINISHED (from ${from} to ${to})`;
  }

  return state;
};

const toTime = (value) => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

// Projects without an end date come first. Then end date descending
// (ongoing / later endings before finished), then start date descending.
const compareProjects = (a, b) => {
  const aEnd = toTime(a.end_date);
  const bEnd = toTime(b.end_date);

  if (aEnd === null && bEnd !== null) return -1;
  if (aEnd !== null && bEnd === null) return 1;
  if (aEnd !== null && bEnd !== null && aEnd !== bEnd) return bEnd - aEnd;

  const aStart = toTime(a.start_date) ?? Number.NEGATIVE_INFINITY;
  const bStart = toTime(b.start_date) ?? Number.NEGATIVE_INFINITY;
  return bStart - aStart;
};

const projectHasSector = (item, sectorParam) => {
  const titles = item.related_sectors_titles;
  if (Array.isArray(titles)) return titles.includes(sectorParam);
  return titles === sectorParam;
};

const filterProjects = (items, projectSubFilter, sectorParam) =>
  items.filter((item) => {
    if (!matchesProjectStatus(item, projectSubFilter)) return false;
    if (!sectorParam || sectorParam === FILTER_ALL) return true;
    if (sectorParam === FILTER_OTHERS) {
      return !hasSectorTitles(item.related_sectors_titles);
    }
    return projectHasSector(item, sectorParam);
  });

const sortProjects = (items) => [...items].sort(compareProjects);

const buildSearchQuery = (
  contentPath,
  activeFilter,
  currentPage,
  itemsPerPage,
  metadataFields,
) => ({
  portal_type: [activeFilter],
  path: contentPath,
  review_state: 'published',
  sort_on: 'getObjPositionInParent',
  sort_order: 'ascending',
  b_size: itemsPerPage,
  b_start: (currentPage - 1) * itemsPerPage,
  metadata_fields: metadataFields,
});

export {
  getRichTextHTML,
  hasSectorTitles,
  getCardLabel,
  getCardCaption,
  filterProjects,
  sortProjects,
  buildSearchQuery,
};
